import { AJUDA_COMECAR } from "@/lib/ajuda/dialogos/comecar";
import { useState, type ReactNode } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@bookdental/ui";
import { useIsMobile, useMediaQuery } from "@/lib/use-media-query";
import { ALTURA_DA_BARRA } from "@/components/layout/mobile/BarraDeAbas";
import { ResumoDaSelecao, type ItemEscolhido } from "./ResumoDaSelecao";

type Resumo = { itens: ItemEscolhido[]; unidade: string; onRemover: (itens: ItemEscolhido[]) => void; onIrPara?: (categoria: string) => void };

/**
 * Escolher itens de uma base em duas colunas: categorias à esquerda, resumo à direita com a ação do
 * passo. Abaixo de `lg` o resumo vira uma barra presa embaixo, que abre a lista completa numa
 * folha. Uma árvore só, pela media query: o resumo não existe duas vezes.
 */
export function LayoutDeSelecao({ children, resumo, acao }: { children: ReactNode; resumo: Resumo; acao: ReactNode }) {
  const largo = useMediaQuery("(min-width: 1024px)");
  const isMobile = useIsMobile();
  const [folha, setFolha] = useState(false);
  const categorias = new Set(resumo.itens.map((i) => i.categoria)).size;

  if (largo) {
    return (
      <div className="grid grid-cols-[minmax(0,1fr)_330px] items-start gap-5 2xl:grid-cols-[minmax(0,1fr)_360px] 2xl:gap-6">
        <div className="min-w-0 space-y-3">{children}</div>
        <ResumoDaSelecao {...resumo} acao={acao} className="sticky top-20 max-h-[calc(100vh-6.5rem)]" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {children}
      <div
        className="sticky z-20 flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 shadow-lg dark:border-border dark:bg-card"
        style={{ bottom: isMobile ? `calc(${ALTURA_DA_BARRA} + 0.75rem + env(safe-area-inset-bottom))` : "1rem" }}
      >
        <button type="button" onClick={() => setFolha(true)} className="min-w-0 flex-1 text-left">
          <span className="block text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">
            {resumo.itens.length} {resumo.unidade}
          </span>
          <span className="block text-[11.5px] font-medium text-brand-600 dark:text-primary">
            {categorias} {categorias === 1 ? "categoria" : "categorias"} · ver lista
          </span>
        </button>
        <div className="shrink-0">{acao}</div>
      </div>
      <Sheet open={folha} onOpenChange={setFolha}>
        <SheetContent
          side="bottom"
          className="flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl border-zinc-200 bg-white p-0 dark:border-border dark:bg-card"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <SheetHeader className="border-b border-zinc-100 px-4 py-3 text-left dark:border-border">
            <SheetTitle className="text-sm font-bold" ajuda={AJUDA_COMECAR.escolhidos}>Escolhidos</SheetTitle>
          </SheetHeader>
          <ResumoDaSelecao
            {...resumo}
            onIrPara={
              resumo.onIrPara
                ? (c) => {
                    setFolha(false);
                    resumo.onIrPara!(c);
                  }
                : undefined
            }
            className="min-h-0 flex-1 rounded-none border-0 shadow-none"
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
